import { Assert } from "../../assert";

export enum Unit {
  MetricTon = 0,
  CubicMeter = 1,
  Kilogram = 2,
  Pound = 3,
  ShortTon = 4,
  LongTon = 5,
}

export const unitStrings: string[] = Object.values(Unit).filter(
  (value): value is string => typeof value === "string"
);

export function convertToDisplayUnit(
  metricTon: number,
  unit: Unit,
  density: number
): number {
  switch (unit) {
    case Unit.MetricTon:
      return metricTon;

    case Unit.Kilogram:
      return metricTon * 1000;

    case Unit.Pound:
      return metricTon * 2204.62262185;

    case Unit.ShortTon:
      return metricTon * 1.10231131092;

    case Unit.LongTon:
      return metricTon * 0.98420652761;

    case Unit.CubicMeter:
      if (density <= 0) {
        throw new Error(
          "Density (kg/m³) is required and must be greater than 0 when converting to cubic meters."
        );
      }

      return (metricTon * 1000) / density;

    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
}

export function convertToTon(
  value: number,
  unit: Unit,
  density: number
): number {
  switch (unit) {
    case Unit.MetricTon:
      return value;

    case Unit.Kilogram:
      return value / 1000;

    case Unit.Pound:
      return value / 2204.62262185;

    case Unit.ShortTon:
      return value / 1.10231131092;

    case Unit.LongTon:
      return value / 0.98420652761;

    case Unit.CubicMeter:
      if (density <= 0) {
        throw new Error(
          "Density (kg/m³) is required and must be greater than 0 when converting from cubic meters."
        );
      }

      return (value * density) / 1000;

    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
}

export function convertToVolume(
  value: number,
  unit: Unit,
  density: number
): number {
  switch (unit) {
    case Unit.CubicMeter:
      return value;

    case Unit.Kilogram:
      if (density <= 0) {
        throw new Error(
          "Density (kg/m³) is required and must be greater than 0 when converting from kilograms."
        );
      }
      return value / density;

    case Unit.MetricTon:
      if (density <= 0) {
        throw new Error(
          "Density (kg/m³) is required and must be greater than 0 when converting from metric tons."
        );
      }
      return (value * 1000) / density;

    case Unit.Pound:
      if (density <= 0) {
        throw new Error(
          "Density (kg/m³) is required and must be greater than 0 when converting from pounds."
        );
      }
      return (value / 2204.62262185) * 1000 / density;

    case Unit.ShortTon:
      if (density <= 0) {
        throw new Error(
          "Density (kg/m³) is required and must be greater than 0 when converting from short tons."
        );
      }
      return (value / 1.10231131092) * 1000 / density;

    case Unit.LongTon:
      if (density <= 0) {
        throw new Error(
          "Density (kg/m³) is required and must be greater than 0 when converting from long tons."
        );
      }
      return (value / 0.98420652761) * 1000 / density;

    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
}
